import dayjs from "dayjs";

export const DATE_DEFAULT_FORMAT = "YYYY-MM-DD HH:mm:ss";
export const DATE_FORMAT = "YYYY-MM-DD";

// Exclusive Utils for Banks
export const TextForTransactionState = Object.freeze({
	"archived": {
		label: "Archivado",
		color: "gray",
	},
	"unverified": {
		label: "No Verificado",
		color: "red",
	},
	"under-review": {
		label: "En Revisión",
		color: "blue",
	},
	"verified": {
		label: "Verificado",
		color: "green",
	},
});

export const TextForInvoicePosition = Object.freeze({
	"in": {
		label: "Venta",
		color: "yellow",
	},
	"out": {
		label: "Compra",
		color: "blue",
	},
});

export const TransactionsType = Object.freeze({
	cash: "Efectivo",
	retention_isr: "Retención ISR",
	retention_iva: "Retención IVA",
	transfer: "Transferencia",
	check: "Cheque",
	exchange_differential: "Diferencial Cambiario",
});

export const extractDepthClassifications = (nodes, depthToConsult = 2, hideArchived = false) => {
	const result = [];
	const traverse = (node) => {		
		if(hideArchived && node.archived){
			return;
		}
		
		if (node.depth === depthToConsult) {
			result.push({
				label: node.classification,
				value: node.id,				
			});
		}
		if (node.children && node.children.length > 0) {
			node.children.forEach(traverse);
		}
	};
	nodes.forEach(traverse);
	return result;
};

export const processClassifications = (classifications = {}, path = "") =>
	Object.entries(classifications).map(([key, children]) => ({
		title: key,
		value: path ? `${path}-${key}` : key,
		key: path ? `${path}-${key}` : key,
		children: typeof children === "object" ? processClassifications(children, path ? `${path}-${key}` : key) : null,
	}));

export const filterTransactionsToShow = (
	transactions = [],
	{firstMonthDetected = "", lastMonthDetected = ""} = {},	
) => {
	if(transactions.length === 0 || firstMonthDetected === "") return transactions;
	
	const [firstYear, firstMonth] = firstMonthDetected.split("-").map(Number);
	const [lastYear, lastMonth] = lastMonthDetected
		? lastMonthDetected.split("-").map(Number)
		: [null, null];

	return transactions.filter((transaction) => {
		const transactionDate = transaction.date?.$d
			? new Date(transaction.date.$d)
			: null;

		if (!transactionDate) return false;

		const transactionYear = transactionDate.getUTCFullYear();
		const transactionMonth = transactionDate.getUTCMonth() + 1;

		if (lastYear && lastMonth) {			
			const isAfterOrEqualFirst = 
				transactionYear > firstYear || 
				(transactionYear === firstYear && transactionMonth >= firstMonth);
			const isBeforeOrEqualLast = 
				transactionYear < lastYear || 
				(transactionYear === lastYear && transactionMonth <= lastMonth);

			return isAfterOrEqualFirst && isBeforeOrEqualLast;
		} else {
			return transactionYear === firstYear && transactionMonth === firstMonth;
		}
	});
};

export const BanksValues = Object.freeze({
	BANRURAL: "bancoBanrural",
	BI: "bancoIndustrial",
	BAM: "bancoAgromercantil",
	BANTRAB: "bancoDeLosTrabajadores",
	GYT: "bancoGyT",
	BAC: "bancoBAC",
	INTERBANCO: "bancoInterBanco",
});

export const OtherBankValue = "otherBank";

export const avaliableBanks = [
	{ label: "Banrural", value: BanksValues.BANRURAL },
	{ label: "Banco Industrial (BI)", value: BanksValues.BI },
	{ label: "Banco Agromercantil (BAM)", value: BanksValues.BAM },
	{ label: "Banco de los Trabajadores (BANTRAB)", value: BanksValues.BANTRAB },
	{ label: "Banco GyT", value: BanksValues.GYT },
	{ label: "Banco de America Central (BAC)", value: BanksValues.BAC },
	{ label: "InterBanco", value: BanksValues.INTERBANCO },
];

export function formatCurrency(value) {
	if (typeof value !== "number") {
		return "";
	}
	
	return value.toLocaleString("en-US", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export function formatToTwoDecimalPlaces(amount) {
	if(typeof amount !== "number"){
		return null;
	}

	return parseFloat(amount.toFixed(2));
}

export const monthNames = [
	"Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
	"Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

export function dateWithTimeDefault(date, currentDate = new Date()){
	const timezoneOffsetMinutes = currentDate.getTimezoneOffset();
	const isUTC = timezoneOffsetMinutes === 0;

	const originalDate = new Date(date);

	if (isUTC) {
		return new Date(Date.UTC(
			originalDate.getFullYear(),
			originalDate.getMonth(),
			originalDate.getDate(),
			12,
			0,
			0
		));
	} else {		
		const utcYear = originalDate.getUTCFullYear();
		const utcMonth = originalDate.getUTCMonth();
		const utcDate = originalDate.getUTCDate();

		return new Date(Date.UTC(
			utcYear,
			utcMonth,
			utcDate,
			12,
			0,
			0
		));
	}
}

export function formatDateStampToString(timestamp, option = 0){
	const milliseconds = (timestamp._seconds * 1000) + (timestamp._nanoseconds / 1e6);
	const dayjsDate = dayjs(new Date(milliseconds));
	const finalFormat = option === 0 ? DATE_DEFAULT_FORMAT : DATE_FORMAT;
	const dateFormat = dayjsDate.format(finalFormat);
	return dateFormat;
}

export function includePercentageAtInvoicesData(invoices = []){
	return invoices.map((data) => {
		const { 
			reconciliation: { in: transactionsCoveringIn = {}, out: transactionsCoveringOut = {} },
			position: positionFromData,
			total: totalFromData,
		} = data;

		const selectedTransactionsCovering = positionFromData === "in" ? transactionsCoveringIn : transactionsCoveringOut;
		const totalCoveredSelectedConvering = Object.values(selectedTransactionsCovering).reduce(
			(accumulator, item) => accumulator += item.amount
			, 0) ?? 0;

		const total = formatToTwoDecimalPlaces(totalFromData);
		const totalCovering = formatToTwoDecimalPlaces(totalCoveredSelectedConvering);
		
		return {
			...data,
			percentage: formatToTwoDecimalPlaces(((totalCovering / total) * 100))
		};
	});
}

export function includePercentageAtBankTransactionsData(transactions = []){
	return transactions.map((data) => {
		const { amount, reconciliation: { invoices = {} } } = data;

		const totalCovered = Object.values(invoices).reduce(
			(accumulator, item)	 => accumulator += item.amount, 0
		) ?? 0;

		const total = formatToTwoDecimalPlaces(amount);
		const totalCovering = formatToTwoDecimalPlaces(totalCovered);
		
		return {
			...data,
			percentage: formatToTwoDecimalPlaces((totalCovering / total) * 100),
		};
	});
}